/** Management, public, and leftover-manager strings. */
export const extraEnMgmt = {
  stockIn: {
    title: 'Stock In',
    subtitle: 'Manage purchase orders and record ingredient deliveries.',
    newPurchase: 'New purchase',
    generateOrders: 'Generate Orders',
    undoToast: 'Order marked as sent.',
    noLines: 'Add at least one ingredient with a quantity greater than 0.',
    duplicateIngredient: 'Each ingredient can only appear once per delivery.',
    noLowStock: 'No ingredients are currently below their low-stock threshold.',
    loadFailed: 'Load failed',
    createFailed: 'Failed to create purchase order.',
    undoTitle: 'Undo purchase?',
    undoBody: 'This will delete the record and revert stock for:',
    undoLine: '{{name}}: −{{qty}} {{unit}}',
    undoConfirm: 'Undo & revert',
    cancelTitle: 'Cancel order?',
    cancelBody: 'This will mark the order as cancelled. No stock changes will be made.',
    cancelConfirm: 'Cancel order',
    generateTitle: 'Generate Orders',
    generateIntro: '{{count}} ingredient(s) below threshold. Assign a supplier and quantity, then generate draft orders.',
    colIngredient: 'Ingredient',
    colInStock: 'In stock',
    colSupplier: 'Supplier',
    colQtyToOrder: 'Qty to order',
    thresholdHint: 'threshold: {{threshold}} {{unit}}',
    supplierNone: '— none —',
    qtyPlaceholder: 'qty',
    needOne: 'Assign a supplier and quantity to at least one ingredient to create orders.',
    createDrafts: 'Create {{count}} draft order(s)',
    pendingTitle: 'Pending Orders',
    colStatus: 'Status',
    colItems: 'Items',
    colCreated: 'Created',
    colNote: 'Note',
    noSupplier: 'No supplier',
    whatsapp: 'WhatsApp',
    whatsappTitle: 'Send to {{name}} via WhatsApp',
    whatsappNoPhone: 'Add a phone number to this supplier to enable WhatsApp',
    receive: 'Receive',
    receiveTitle: 'Confirm goods received — updates inventory',
    cancelOrderTitle: 'Cancel this order',
    confirmDelivery: 'Confirm delivery',
    captionReceive: 'Adjust quantities to what actually arrived, then confirm to add it to stock.',
    captionNew: 'Record ingredients you bought. Stock and cost prices update as soon as you confirm.',
    footerLines: '{{count}} line(s)',
    footerTotal: ' · Total {{amount}}',
    confirmReceipt: 'Confirm receipt',
    confirmPurchase: 'Confirm purchase',
    supplier: 'Supplier',
    note: 'Note',
    notePlaceholder: 'Invoice #, delivery details…',
    colQtyReceived: 'Quantity received',
    colUnitCost: 'Unit cost (AMD)',
    typeIngredient: 'Type ingredient…',
    noMatch: 'No matching ingredient',
    inStock: 'In stock: {{stock}} {{unit}}',
    unitCostPlaceholder: 'Unit cost ֏',
    removeLine: 'Remove line',
    addIngredient: 'Add ingredient',
    historyTitle: 'Purchase history',
    purchaseCount: '{{count}} purchase(s)',
    empty: 'No purchases recorded yet.',
    emptyRange: 'No purchases in the selected date range.',
    colDate: 'Date',
    colTotalCost: 'Total cost',
    colAddedBy: 'Added by',
    undoTitleRow: 'Undo and revert stock',
    statusDraft: 'draft',
    statusSent: 'sent',
    statusReceived: 'received',
    statusCancelled: 'cancelled',
    csvDate: 'Date',
    csvSupplier: 'Supplier',
    csvIngredient: 'Ingredient',
    csvQuantity: 'Quantity',
    csvUnit: 'Unit',
    csvUnitCost: 'Unit cost (AMD)',
    csvLineTotal: 'Line total (AMD)',
    csvAddedBy: 'Added by',
    csvNote: 'Note',
  },
  prepBatches: {
    title: 'Prep Batches',
    subtitle: 'Log a prep batch to deduct raw ingredients, update prep stock, and roll batch cost into the prep ingredient price.',
    recalcAll: 'Recalculate all prep costs',
    recalcTitle: 'Set each prep ingredient cost from its recipe BOM and current raw prices',
    recalcConfirm:
      'Recalculate prep ingredient costs from current recipes and raw ingredient prices? Batch history and stock are not changed.',
    recalcUpdated: 'Updated {{count}}: {{list}}',
    recalcSkipped: 'Skipped {{count}}: {{list}}',
    recalcNothing: 'Nothing to update.',
    recalcFailed: 'Recalculation failed',
    logPanel: 'Log a Prep Batch',
    selectRecipe: 'Select prep recipe…',
    recipeOption: '{{name}} (yields {{yield}} {{unit}})',
    qtyPlaceholder: 'Qty produced',
    qtyWithUnit: 'Qty produced ({{unit}})',
    notePlaceholder: 'Note (optional)',
    previewTitle: 'Ingredients to deduct for {{qty}} {{unit}}',
    previewYield: '(recipe quantities are for {{yield}} {{unit}})',
    colIngredient: 'Ingredient',
    colDeduct: 'Deduct',
    colInStock: 'In stock',
    low: 'low',
    subRecipeWarning: 'Sub-recipes are not resolved — add their ingredients directly to this prep recipe.',
    insufficient: 'Some ingredients have insufficient stock. Stock will be floored at 0.',
    batchCost: 'Batch raw cost: {{raw}} → {{unitCost}}/{{unit}} prep output',
    missingCost: 'Missing raw cost: {{names}}',
    reviewConfirm: 'Review & Confirm',
    confirmPrompt: 'Confirm logging {{qty}} {{unit}} of {{name}}?',
    confirm: 'Confirm',
    success: 'Logged {{qty}} {{unit}} of {{name}}',
    logFailed: 'Failed to log batch',
    emptyRecipes: 'No prep recipes yet. Go to Recipes and enable a recipe as a prep.',
    recipesLink: 'Recipes',
    historyTitle: 'Batch history',
    empty: 'No batches logged yet.',
    colDate: 'Date',
    colPrep: 'Prep',
    colQty: 'Qty produced',
    colBy: 'By',
    colNote: 'Note',
  },
  modifiers: {
    title: 'Modifiers',
    subtitle: 'Add selectable options to products — ice, take away, extra shot, etc. Attach each group only to the products where it applies.',
    howTitle: 'How modifiers work',
    step1: 'Create a group — e.g. Preparation (min 0, max 1 = optional) or Service (min 1, max 1 = cashier must pick).',
    step2: 'Add options — e.g. No ice, Extra ice, Take away, For here.',
    step3: 'Attach to products — products without an attachment never show that group.',
    footer: 'Selected options appear on the printed check and kitchen ticket. Price = base product + option deltas.',
    addGroup: 'Add modifier group',
    namePlaceholder: 'Group name (e.g. Bean Type)',
    min: 'Min',
    max: 'Max',
    submit: 'Add group',
    groupNameRequired: 'Group name is required.',
    optionNameRequired: 'Option name is required.',
    deleteConfirm: 'Delete this modifier group and all its options?',
    empty: 'No modifier groups yet. Create one above.',
    pickRule: 'pick {{rule}}',
    required: ' (required)',
    optional: ' (optional)',
    options: 'Options',
    optionName: 'Option name',
    deltaKind: 'Price change',
    kindFixed: '֏',
    kindPercent: '%',
    deltaPlaceholder: 'Drams, e.g. 1500 or -200',
    deltaPlaceholderPercent: 'Percent, e.g. 10 or -10',
    deltaInvalid: 'Enter a number. Use a minus for a discount.',
    addOption: 'Add option',
    noOptions: 'No options yet.',
    colOption: 'Option',
    colPriceDelta: 'Price delta',
    attached: 'Attached products',
    noAttached: 'No products attached yet.',
    attachPlaceholder: '+ Attach a product…',
    removeTitle: 'Remove {{name}}',
  },
  loyalty: {
    title: 'Loyalty',
    subtitleDisabled: 'Coin rewards + Apple/Google Wallet member cards.',
    subtitleEnabled: 'Earn & redeem {{pointName}}, member discounts, and wallet passes with customer numbers.',
    notAvailableTitle: 'Not available yet',
    notAvailableBody: 'Loyalty has not been enabled for your cafe. Ask your Orio administrator to turn it on.',
    settings: 'Program settings',
    enable: 'Enable loyalty program',
    earnRate: 'Earn rate',
    earnHint: 'AMD spent to earn 1 point.',
    redeemRate: 'Redeem rate',
    redeemHint: 'AMD off per 1 point. Keep much lower than earn.',
    rateSummary: 'Rate summary',
    hideRateSummary: 'Hide rate summary',
    saveSettings: 'Save settings',
    saved: 'Saved.',
    notProvisioned: 'Loyalty is not provisioned for this cafe — contact your administrator.',
    exampleTitle: 'Example with current rates',
    exampleSpend: 'Customer spends {{amount}} → earns {{coins}} {{pointName}} (1 per {{earnRate}} AMD).',
    walletTitle: 'Wallet pass design',
    logoText: 'Pass logo text',
    logoPlaceholder: 'Cafe name',
    logoHint: 'Shown at the top of the Apple/Google Wallet card.',
    passColor: 'Pass color',
    colorApply: 'Applies to new passes. Existing passes keep their design.',
    saveDesign: 'Save design',
    colorDark: 'dark',
    colorBlue: 'blue',
    colorGreen: 'green',
    colorRed: 'red',
    colorPurple: 'purple',
    colorOrange: 'orange',
    linksTitle: 'Signup links',
    newLink: '+ New link',
    namePlaceholder: 'Name (e.g. VIP tier)',
    collectPoints: 'Collect points',
    customNumber: 'Custom #',
    add: 'Add',
    discountByCategory: 'Discount by menu category',
    collect: 'Collect',
    custom: 'Custom',
    colName: 'Name',
    colDiscount: 'Discount',
    colPoints: 'Points',
    colCustomNumber: 'Custom #',
    colLink: 'Link',
    colStatus: 'Status',
    copyLink: 'Copy link',
    disableTitle: 'Click to disable signups',
    enableTitle: 'Click to re-enable',
    emptyLinks: 'No links yet. Create one to let customers join.',
    created: 'Signup link created.',
    updated: 'Link updated. New signups get the new terms; existing members keep theirs.',
    duplicateName: 'A link with that name already exists.',
    noCategories: 'No menu categories yet.',
    allCategories: 'All categories',
    mixed: 'Mixed',
    membersTitle: 'Members ({{filtered}})',
    removeSelected: 'Remove selected ({{count}})',
    addMember: '+ Add member',
    source: 'Source',
    allSources: 'All sources',
    manual: 'Manual',
    linkRemoved: 'Link removed',
    phoneOptional: 'Phone (optional)',
    create: 'Create',
    enterName: 'Enter a name for the member.',
    memberCreated: 'Member created.',
    emptyMembers: 'No members yet.',
    emptyFilter: 'No members match this source filter.',
    qr: 'QR',
    remove: 'Remove',
    colCustomerNumber: 'Customer #',
    colEarnsPoints: 'Earns points',
    colWallet: 'Wallet',
    colJoined: 'Joined',
    qrTitle: 'Member QR',
    qrBody: 'Customer number {{code}} — no wallet pass. Print or share this QR for POS lookup.',
    actionsAria: 'Actions',
    linkNameAria: 'Link name',
    memberNameAria: 'Member name',
    selectAllAria: 'Select all visible members',
    selectMemberAria: 'Select {{name}}',
    customNumberTitle: 'Signups can choose their customer number (1000+)',
    uniqueCodeFailed: 'Could not generate a unique customer number. Try again.',
    manualMembersNote: 'Manually added members get a customer number and QR code only — no wallet pass.',
    discountPercentAria: '{{name}} discount percent',
    editDiscounts: 'Edit category discounts below',
    cardTitleAlways: 'Card title is always {{title}}.',
    balanceShown: 'Balance is shown as {{name}}.',
  },
  loyaltyJoin: {
    pageTitle: 'Join rewards',
    pageTitleCafe: 'Join rewards — {{cafeName}}',
    unavailable: 'Signup is temporarily unavailable.',
    notConfigured: 'App is not configured.',
    incompleteLink: 'This signup link is incomplete. Ask the cafe for their full rewards link.',
    loadFailed: 'Could not load cafe.',
    cafeNotFound: 'Cafe not found.',
    notAvailable: 'Loyalty is not available for this cafe yet.',
    notOpen: 'Loyalty signup is not open yet — check back soon.',
    invalidLink: 'This signup link is not valid or has been disabled.',
    loadPageFailed: 'Could not load signup page.',
    enterName: 'Enter your name.',
    enrollNotConfigured: 'Signup is not configured on the server yet.',
    noWalletLink: 'No wallet link returned. Try again or ask the cafe.',
    signupFailed: 'Signup failed.',
    youGet: 'You get {{benefit}}. ',
    earnPoints: 'Earn {{pointName}} when you pay — 1 per {{earnRate}} AMD spent.',
    discountOnly: 'This membership does not collect points — discount only.',
    successTitle: 'Add to your wallet',
    customerNumber: 'Your customer number: {{code}}',
    savePass: 'Save the pass below — it includes your QR code.',
    addWallet: 'Add to Apple / Google Wallet',
    defaultTitle: 'Join rewards',
    nameLabel: 'Your name',
    namePlaceholder: 'Full name',
    phoneLabel: 'Phone (optional)',
    phonePlaceholder: '91 123456',
    phoneAria: 'Phone number without country code',
    customNumberLabel: 'Customer number (optional, {{min}}+)',
    customNumberPlaceholder: 'Leave blank for auto, or e.g. {{min}}',
    signingUp: 'Signing up…',
    signUp: 'Sign up',
    leadPoints: 'Digital wallet pass with your customer number. Earn points on every visit.',
    leadDiscount: 'Digital wallet pass with your customer number and member discounts.',
    plus: ' plus ',
    pctOffCategory: '{{pct}}% off {{name}}',
    upToOff: 'Up to {{pct}}% off selected categories',
    pctOffEvery: '{{pct}}% off every visit',
  },
  settings: {
    title: 'Settings',
    taxTitle: 'Armenian Tax Service credentials',
    taxIntro: 'Stored securely in your account and used to fetch invoices. Only you (the owner) can view or change them.',
    tin: 'TIN (taxpayer ID)',
    tinPlaceholder: 'e.g. 08319907',
    username: 'Username',
    usernamePlaceholder: 'Tax service login',
    password: 'Password',
    activeToken: 'Active tax service token',
    tokenHint: 'This is the JWT used to call the tax service. It is shared across all devices logged in to this account.',
    saved: 'Saved.',
    posTitle: 'POS device password',
    posIntro:
      'Enter this password once on a new POS device to enroll it. The device will remember the café and cashier list — cashiers only need their PIN after that.',
    posPassword: 'Password',
    posPlaceholder: 'Set a POS password…',
    generate: 'Generate',
  },
  users: {
    title: 'Users',
    subtitle: 'Create dashboard accounts for staff who help manage the business.',
    rolesTitle: 'Role access',
    roleAdmin: 'Admin — Insights, Orders, Shifts, Menu, Ingredients, Recipes, Receipt, Invoices',
    roleManager: 'Manager — Orders, Shifts, Menu, Ingredients, Recipes, Receipt, Invoices (no Insights)',
    roleAccountant: 'Accountant — Books only. Can download the ArmSoft pack. Cannot change operations.',
    rolesFooter: 'None of these roles can access Users, Cashiers, or Settings.',
    createTitle: 'Create user',
    emailPlaceholder: 'Email address',
    namePlaceholder: 'Full name (optional)',
    roleManagerOpt: 'Manager',
    roleAdminOpt: 'Admin',
    roleAccountantOpt: 'Accountant',
    submit: 'Create user',
    emailRequired: 'Email is required.',
    credsTitle: 'New user credentials',
    credsNote: 'Copy and send these to the user. Password is shown only once.',
    email: 'Email',
    password: 'Password',
    emptyTitle: 'No users yet',
    empty: 'No users yet. Create one above.',
    count: '{{count}} user(s)',
    colFullName: 'Full name',
    colEmail: 'Email',
    colRole: 'Role',
    colCreated: 'Created',
    resetPassword: 'Reset password',
    resetCreds: 'New credentials — send to user, shown once:',
    resetting: 'Resetting…',
    copyEmail: 'Copy email',
    copyPassword: 'Copy password',
    copied: 'Copied',
  },
  clients: {
    title: 'Clients',
    subtitle: 'Create a cafe/restaurant and its owner account.',
    newTitle: 'New client',
    cafeName: 'Cafe name',
    cafePlaceholder: 'Demo Cafe',
    ownerEmail: 'Owner email',
    ownerEmailPlaceholder: 'owner@cafe.com',
    ownerName: 'Owner name',
    ownerNamePlaceholder: 'Full name',
    submit: 'Create client',
    required: 'Cafe name and owner email are required.',
    credsTitle: '{{name}} — owner credentials',
    credsNote: 'Copy and send these to the owner. They are shown only once.',
    copyCreds: 'Copy credentials',
    allClients: 'All clients',
    emptyTitle: 'No clients yet',
    empty: 'Create your first cafe above.',
    colCafe: 'Cafe',
    colOwnerEmail: 'Owner email',
    colLoyalty: 'Loyalty',
    colTableService: 'Table service',
    colBookings: 'Bookings',
    colWalletPasses: 'Wallet passes',
    colActive: 'Active',
    colCreated: 'Created',
    rename: 'Rename',
    rowActions: 'Client actions',
    newPassword: 'New password',
    resetConfirm: 'Reset owner password for {{name}}? This will immediately invalidate the current password.',
    resetYes: 'Yes, reset',
    resetFailed: 'Reset failed',
    statsActiveToday: 'Selling today',
    statsActiveSub: '{{selling}} of {{total}} active',
    statsOrdersToday: 'Orders today',
    statsQuiet: 'Quiet cafes',
    statsQuietSub: 'No order in 3 days',
    statsForce30: 'Force-closed, 30 days',
    statsRevenue30: 'Revenue, 30 days',
    colLastOrder: 'Last order',
    colOrders30: 'Orders, 30 days',
    colRegisters: 'Registers',
    openStats: 'Open usage',
    freshToday: 'Today',
    freshYesterday: 'Yesterday',
    freshDays: '{{n}}d ago',
    freshNever: 'No orders',
    versionNone: 'None yet',
    versionCount: '{{version}} × {{n}}',
    sectionActivity: 'Activity',
    sectionHealth: 'Health',
    sectionUsage: 'Usage',
    sectionRegisters: 'Registers',
    ordersToday: 'Orders today',
    orders7: 'Orders, 7 days',
    orders30: 'Orders, 30 days',
    openShifts: 'Open shifts',
    lastShift: 'Last shift opened',
    lastSignIn: 'Last sign-in',
    force30: 'Force-closed, 30 days',
    forceSystem: 'Paid, system error',
    forceMistake: 'Mistake',
    forceFree: 'Free',
    forceOther: 'Other reason',
    paidNoFiscal: 'Paid, no fiscal receipt',
    paidNoPayment: 'Paid, no payment row',
    diagnostics30: 'Diagnostics sent, 30 days',
    cashiers: 'Cashiers',
    dashboardUsers: 'Dashboard users',
    products: 'Products on the menu',
    categories: 'Categories',
    ingredients: 'Ingredients',
    recipes: 'Recipes',
    loyaltyMembers: 'Loyalty members',
    bookingsMonth: 'Bookings this month',
    invoicesMonth: 'Invoices this month',
    revenue30: 'Revenue, 30 days',
    registersEmpty: 'No register has checked in yet. This shows up after the next POS update.',
    colVersion: 'Version',
    colPlatform: 'Platform',
    colLastSeen: 'Last seen',
    colPending: 'Waiting to sync',
    colLastGap: 'Last silence',
    colOffline30: 'Offline, 30 days',
    seenJustNow: 'Just now',
    seenMinutes: '{{n}}m ago',
    seenHours: '{{n}}h ago',
    seenDays: '{{n}}d ago',
    minutesShort: '{{n}}m',
    hoursShort: '{{n}}h',
    hoursMinutes: '{{hours}}h {{minutes}}m',
    platformMacos: 'Mac',
    platformWindows: 'Windows',
    platformWeb: 'Browser',
    platformDesktop: 'Desktop',
    never: 'Never',
  },
  invoices: {
    title: 'Invoices',
    subtitle: 'Supplier tax invoices from the Tax Service. Review, categorise and approve them internally.',
    connecting: 'Connecting…',
    loadingCount: 'Loading… {{count}}',
    tokenMissing: 'Tax Service credentials not set — go to Settings to add them.',
    invoiceCount: '{{count}} invoice(s)',
    total: 'Total {{amount}}',
    supplierSummary: 'Supplier summary →',
    supplier: 'Supplier',
    supplierTin: 'Supplier TIN',
    serial: 'Serial number',
    category: 'Category',
    include: 'Include',
    exclude: 'Exclude',
    selectAll: 'Select all',
    clear: 'Clear',
    minTotal: 'Min total ֏',
    taxStatusAll: 'Tax status: all',
    taxIssued: 'Issued',
    taxApproved: 'Approved',
    internalAll: 'Internal: all',
    pending: 'Pending',
    approved: 'Approved',
    reverted: 'Reverted',
    approvedByAll: 'Approved by: anyone',
    reset: 'Reset',
    noMatchTitle: 'No invoices match these filters',
    noMatchText: 'Loosen a filter or reset them to see everything in this period.',
    resetFilters: 'Reset filters',
    colNum: '#',
    colSerial: 'Serial',
    colSupplier: 'Supplier',
    colCategory: 'Category',
    colDate: 'Date',
    colTotal: 'Total',
    colTaxStatus: 'Tax status',
    colInternal: 'Internal',
    colApprovedBy: 'Approved by',
    uncategorised: 'Uncategorised',
    review: 'Review',
    revert: 'Revert',
    revertConfirm: 'Revert?',
    emptyTitle: 'No invoices in this period',
    emptyText: 'Pick a wider date range, or check that the Tax Service credentials in Settings are still valid.',
    reviewTitle: 'Review Invoice',
    approveHint: 'Approving internally updates your ingredient stock. The Tax Service status is not changed.',
    loadingGoods: 'Loading goods…',
    colGoodsName: 'Goods name (from invoice)',
    colInvoiceQty: 'Invoice qty',
    colInvoicePrice: 'Invoice price',
    colRecordAs: 'Record as',
    colUnit: 'Unit',
    colPrice: 'Price',
    colLinkIngredient: 'Link to ingredient',
    existing: 'Existing',
    createNew: 'Create new',
    selectIngredient: '— select ingredient —',
    ingredientName: 'Ingredient name',
    addIngredient: '+ Add ingredient',
    noLineItems: 'No individual line items — approve at invoice level.',
    alreadyApproved: 'This invoice is already {{status}} — no further action needed.',
    approveInternal: 'Approve Internally',
    confirmPrompt: 'This will record stock delivery and cannot be undone. Confirm?',
    approvalNote: 'Approval note (optional)',
    yesApprove: 'Yes, approve',
    approvedMsg: 'Approved!',
    csvSupplierTin: 'Supplier TIN',
    csvTotalAmd: 'Total (AMD)',
    csvInternalStatus: 'Internal status',
    csvApprovedAt: 'Approved at',
    revertedBy: 'Reverted by {{name}}',
    metaNote: 'Note',
    metaTotal: 'Total',
    metaVat: 'VAT',
    metaTax: 'Tax',
    delivery: 'Delivery',
    authPerson: 'Auth',
    shipFrom: 'Ship from',
    shipTo: 'Ship to',
    colTrack: 'Track',
    trackTitle: 'Track this item in inventory',
    useSingle: 'Use single ingredient',
    splitAcross: 'Split across ingredients',
    splitQty: 'Split qty: {{sum}} / {{qty}} invoice',
    lastPrice: 'Last',
    ingredientCount: '{{count}} ingredient(s)',
    qtyPlaceholder: 'Qty',
    splitIngredient: '— ingredient —',
    splitNew: 'New',
    newIngredientName: 'New ingredient name',
    removeSplit: 'Remove',
    trackRequired: 'All tracked lines must be linked to ingredient(s) (or skipped)',
    approvalNotePlaceholder: 'e.g. partial delivery, price check…',
    credentialsMissing: 'Tax credentials not configured. Go to Settings.',
    taxSessionActive:
      'Tax Service Error 0032: a previous session is still active. Wait a few minutes for it to expire, then refresh the page.',
  },
  purchases: {
    title: 'Purchases',
    subtitle: 'Spend on approved e-invoices by supplier category. Assign categories on Suppliers or Invoices.',
    loadFailed: 'Failed to load purchases',
    couldntLoad: "Couldn't load purchases",
    totalSpend: 'Total spend',
    approvedInvoices: 'Approved invoices',
    suppliers: 'Suppliers',
    chartTitle: 'Monthly spend by category',
    nothingToChart: 'Nothing to chart',
    bySupplier: 'Spend by supplier',
    noSuppliers: 'No suppliers yet',
    colInvoices: 'Invoices',
    colSpend: 'Spend',
    colShare: 'Share',
    uncategorized: 'Uncategorized',
    emptyPeriod: 'No approved invoices in this period.',
    openSummary: 'Open purchase summary',
  },
  supplierDetail: {
    viewInvoices: 'View invoices',
    loadFailed: 'Failed to load supplier purchases',
    totalSpend: 'Total spend',
    approvedInvoices: 'Approved invoices',
    itemsSupplied: 'Items supplied',
    empty: 'No approved invoices from this supplier in the selected period.',
    colItem: 'Item',
    colQuantity: 'Quantity',
    colInvoices: 'Invoices',
    colAvgPrice: 'Avg price',
    colLastPrice: 'Last price',
    colTrend: 'Trend',
    colSpend: 'Spend',
    colShare: 'Share',
    untracked: 'untracked',
    tinPrefix: 'TIN {{tin}}',
  },
  settingsPage: {},
  wasteLog: {
    newEntry: 'New entry',
    newCaption: 'Stock is reduced immediately.',
    compRecipient: 'Comp recipient',
    commentRequired: 'Add a comment for Other.',
    commentPlaceholder: 'Comment required',
    selectRecipient: 'Select name…',
    recipientRequired: 'Pick who this staff meal is for.',
    cancelEntry: 'Cancel entry',
    editTitle: 'Edit waste',
    quantity: 'Quantity',
    reason: 'Reason',
    note: 'Note',
    cancelTitle: 'Cancel this waste entry?',
    cancelMessage: 'Restore {{qty}} {{unit}} of {{name}} to stock. This cannot be undone.',
    cancelled: 'Cancelled',
    cancelling: 'Cancelling…',
  },
  stockCount: {
    revertModalTitle: 'Revert stock count',
    revertModalBody:
      "Undoes this count's effect on current stock ({{delta}} {{unit}} reversed). The original stays in history but is excluded from loss metrics.",
    ingredient: 'Ingredient',
    countDate: 'Count date',
    countDelta: 'Count delta',
    currentStock: 'Current stock',
    afterRevert: 'After revert',
    reasonOptional: 'Reason (optional)',
    reasonPlaceholder: 'e.g. Wrong quantity entered',
    confirmRevert: 'Revert count',
  },
  cashiers: {
    addPanel: 'Add cashier',
    hdmPlaceholder: 'HDM no',
    hdmCol: 'HDM no',
    hdmShort: 'HDM',
    username: 'Username',
    pin: 'PIN',
    newPinPlaceholder: 'new PIN',
    colUsername: 'Username',
    colName: 'Name',
    colLastSeen: 'Last seen',
    deleteConfirm: 'Delete this cashier? This cannot be undone.',
    pinInvalid: 'PIN must be exactly 4 digits.',
    usernameRequired: 'Username is required.',
    empty: 'No cashiers yet.',
    resetPin: 'Reset PIN',
  },
  menu: {
    noClient: 'No client context for this account.',
    categoryNameRequired: 'Category name is required.',
    hdmDeptInvalid: 'HDM department must be a whole number ≥ 1.',
    deleteCategoryConfirm: 'Delete this category? Products in it will lose their category.',
    productRequired: 'Name, category, and price are required.',
    importNoRows: 'No valid rows found (each needs a name, category with HDM dept, and price).',
    imported: 'Imported {{count}} product(s).',
    namePlaceholder: 'Name',
    sortPlaceholder: 'Sort',
    hdmPlaceholder: 'HDM dept *',
    hdmTitle: 'HDM fiscal department id (must match your device)',
    printKitchen: 'Print kitchen',
    colName: 'Name',
    colOrder: 'Order',
    colHdm: 'HDM dept',
    colKitchen: 'Kitchen',
    colRegister: 'Register',
    hideFromRegister: 'Hide from the register',
    showOnRegister: 'Show on the register',
    confirmHideTitle: 'Hide {{name}} from the register?',
    confirmHideBody: 'It will leave the POS register and search. The category stays in the menu.',
    confirmShowTitle: 'Show {{name}} on the register?',
    confirmShowBody: 'It will appear on the POS register again.',
    noCategories: 'No categories yet. Add one above.',
    categoryPlaceholder: 'Category…',
    pricePlaceholder: 'Price',
    unitPlaceholder: 'Unit',
    adgPlaceholder: 'ADG code',
    codePlaceholder: 'Code',
    noRecipe: 'No recipe',
    colProduct: 'Product',
    colCategory: 'Category',
    colPrice: 'Price',
    colUnit: 'Unit',
    colAdg: 'ADG',
    colRecipe: 'Recipe',
    colCost: 'Cost',
    colStatus: 'Status',
    noProducts: 'No products yet. Add categories first, then products.',
    noSearchMatch: 'No products match "{{query}}".',
    emptyCategory: 'No products in this category.',
    importHint: 'Columns: name, category, price, unit, adg_code, code',
    deptColorTitle: 'POS tab color for dept {{id}}',
  },
  ingredients: {
    exportCsv: 'Export CSV',
    copy: 'Duplicate',
    noClient: 'No client context.',
    nameRequired: 'Name is required.',
    invalidNumber: 'Enter a valid number.',
    invalidNonNegative: 'Enter a valid non-negative number.',
    invalidDelta: 'Enter a non-zero delta (use negative for waste).',
    namePlaceholder: 'Name',
    lowStockPlaceholder: 'Low-stock alert',
    costPlaceholder: 'Cost (AMD)',
    openingStockPlaceholder: 'Opening stock',
    colReorder: 'Reorder qty',
    colValue: 'Value',
    prep: 'Prep',
    changeUnit: 'Change unit',
    historyTitle: 'History — {{name}}',
    openInvoice: 'Open invoice',
    invoiceMissing: 'That invoice is no longer on file.',
    suppliers: 'Suppliers:',
    noSuppliers: 'None recorded',
    noTx: 'No transactions yet.',
    goToStockIn: 'Go to Stock In →',
    markActive: 'Mark active',
    markInactive: 'Mark inactive',
    viewHistory: 'View history',
    lowStockBanner: '{{count}} ingredient(s) below threshold:',
    moreCount: ' +{{count}} more',
    set: 'Set',
    newValuePlaceholder: 'new value',
    adjustQtyPlaceholder: '±qty',
    notePlaceholder: 'note',
    ok: 'OK',
    adjustTitle: 'Adjust (waste / correction)',
    prepManaged: 'Managed by Prep Batches',
    prepManagedHint: 'Managed via Prep Batches — edit as admin/owner',
    csvHint: 'Columns: name, unit, stock, low_stock_threshold, cost_price (header row optional)',
    deleteTitle: 'Delete ingredient?',
    deleteBody: 'Delete {{name}}? This cannot be undone.',
    checking: 'Checking…',
    cannotDeleteTitle: 'Cannot delete "{{name}}"',
    cannotDeleteBody:
      'This ingredient is still referenced in the following places. Remove it from each before deleting.',
    changeUnitTitle: 'Change unit — {{name}}',
    newUnit: 'New unit',
    conversionLabel: 'Conversion — 1 {{newUnit}} = how many {{oldUnit}}?',
    conversionHint: 'set to 1 for a simple unit rename with no recalculation',
    previewChanges: 'Preview of changes',
    field: 'Field',
    current: 'Current',
    newCol: '→ New',
    costPriceField: 'Cost price',
    recipeLine: 'Recipe: {{name}}',
    choiceLine: 'Choice: {{name}}',
    ledgerRepairNote:
      'Ledger repair only — stock and recipes stay as-is. Large legacy transaction quantities (ml-scale, ≥ ratio ÷ 4) are divided by {{ratio}}; small rows already in {{unit}} (e.g. recent +20 restocks, −1 sales) are left unchanged.',
    convertNote:
      'Stock, recipes, cost, and all inventory transaction history are converted using this ratio. Use ledger repair (same unit + ratio) if you already changed the unit but stock-out totals still look wrong.',
    positiveRatio: 'Enter a positive conversion factor.',
    confirmApply: 'This will update all the values above. Are you sure?',
    yesApply: 'Yes, apply',
    renameTo: 'Rename to "{{unit}}"',
    repairLedgerBtn: 'Repair ledger ({{unit}})',
    convertTo: 'Convert to {{unit}}',
    colTime: 'Time',
    colType: 'Type',
    colDelta: 'Delta',
    colReference: 'Reference',
    colNote: 'Note',
    historyPageTotal: '{{total}} total',
    unitChangeFailed: 'Unit change failed',
    noValidRows: 'No valid rows found.',
    imported: 'Imported {{count}} ingredient(s).',
    inStockSuffix: '{{stock}} in stock',
    blockRecipe: 'Recipe: "{{name}}"',
    blockSubstitute: 'Substitute option in recipe: "{{name}}"',
    blockPoItems: '{{count}} purchase order item(s)',
    csvName: 'Name',
    csvUnit: 'Unit',
    csvStock: 'Stock',
    csvLowStock: 'Low-stock threshold',
    csvReorder: 'Reorder qty',
    csvCost: 'Cost (AMD)',
    csvValue: 'Value (AMD)',
    csvActive: 'Active',
    csvYes: 'yes',
    csvNo: 'no',
  },
  recipes: {
    cardNameRequired: 'Card name is required.',
    failed: 'Failed',
    deleteConfirm: 'Delete recipe "{{name}}"? This cannot be undone.',
    saveName: 'Save name',
    newTitle: 'New recipe',
    newCaption: 'Give it a name, then add ingredients in the editor.',
    name: 'Name',
    batchYield: 'Batch yield',
    unit: 'Unit',
    prepRecipe: 'Prep recipe',
    prepHint: 'made in batches and stocked as an ingredient',
    yieldPlaceholder: 'e.g. 1.5',
    unitPlaceholder: 'Unit…',
    noMatch: 'No recipes match "{{query}}".',
    noMatchTitle: 'No recipes yet',
    noMatchText: 'Create one to start tracking stock per sale.',
    rename: 'Rename',
    delete: 'Delete',
    prep: 'Prep',
    back: 'Back',
    selectTitle: 'Select a recipe',
    soldDirectly: '· sold directly',
    ingredientsTitle: 'Ingredients',
    emptyTitle: 'No ingredients yet',
    emptyText: 'Add the first line below. Stock is deducted from these on every sale.',
    choiceTitle: 'Choice group',
    addChoice: 'Add choice group',
    pickOne: 'Pick one',
    choiceMode: 'How picks are added',
    choiceModeSeparate: 'Separate items',
    choiceModeCombine: 'On this item',
    choiceModeSeparateHint: 'Each pick is added as its own line — wine pours, bottles.',
    choiceModeCombineHint: 'Picks stay listed on this item — cheese plate, mixed board.',
    choiceMin: 'Min',
    choiceMax: 'Max',
    choiceMaxNone: 'No limit',
    yield: 'Yield',
    substitutePlaceholder: 'Substitute ingredient…',
    pricePlus: '+ price ֏',
    deleteRecipe: 'Delete recipe',
    editSubstitute: 'Edit substitute',
    removeSubstitute: 'Remove substitute',
    editLine: 'Edit line',
    qty: 'Qty',
    choiceHint: 'Cashier picks from these ingredients at the POS. Separate items = wine. On this item = cheese plate.',
    editDeduction: 'Edit deduction qty',
    removeOption: 'Remove option',
    ingredientPlaceholder: 'Ingredient…',
    searchIngredient: 'Search ingredient…',
    ingredientNoMatch: 'No ingredients match',
    qtyPerBatch: 'Qty per batch',
    makePrep: 'Make it a prep recipe',
    saveForSubstitutes: 'Save to manage substitutes',
    deleteGroup: 'Delete group',
    noOptions: 'No options yet.',
    addOption: 'Add option',
    yields: 'Yields',
    linkedAuto: 'linked ingredient auto-managed',
    autosaved: 'Saved',
  },
  bookings: {
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    notePlaceholder: 'Late arrival, allergies, special requests…',
    couldntLoad: "Couldn't load booking insights",
  },
  rooms: {
    pricePlaceholder: 'AMD / night',
  },
  suppliers: {
    openSummary: 'Open purchase summary',
    phonePlaceholder: '+374 XX XXX XXX',
  },
  staffComps: {
    couldntLoad: "Couldn't load staff comps",
  },
  managers: {
    title: 'Managers',
    subtitle: "Create dashboard accounts for staff who manage operations but don't own the business.",
    accessTitle: 'Manager access',
    canAccess: 'Can access:',
    canAccessList: 'Orders, Shifts, Menu, Ingredients, Tech cards, Receipt',
    cannotAccess: 'Cannot access:',
    cannotAccessList: 'Insights (Dashboard, Sales, Menu/Staff/Inventory analytics), Managers, Cashiers',
    signInNote:
      'Managers sign in at the same dashboard URL using their email and a temporary password you relay to them.',
    createTitle: 'Create manager',
    emailPlaceholder: 'Email address',
    namePlaceholder: 'Full name (optional)',
    creating: 'Creating…',
    create: 'Create manager',
    credsTitle: 'Manager credentials',
    credsNote: 'Copy and send these to the manager. Password is shown only once.',
    email: 'Email',
    password: 'Password',
    copy: 'Copy',
    copied: 'Copied',
    emptyTitle: 'No managers yet',
    count: '{{count}} manager(s)',
    empty: 'No managers yet. Create one above.',
    colName: 'Full name',
    colEmail: 'Email',
    colPos: 'POS access',
    colCreated: 'Created',
    pinPlaceholder: 'PIN (auto if blank)',
    cashierNo: 'Cashier #',
    save: 'Save',
    pinSet: 'PIN set',
    noPos: 'No POS access',
    changePin: 'Change PIN',
    setPin: 'Set PIN',
    newPin: 'New PIN: {{pin}} — tell the manager',
    loading: 'Loading…',
  },
  customerDisplay: {
    joinPlaceholder: 'https://yoursite.com/join/…',
  },
  receipt: {
    popupBlocked: 'Pop-up blocked. Allow pop-ups to print the receipt.',
  },
  books: {
    title: 'Books',
    subtitle: 'Daily figures for ArmSoft. This screen is not a ledger.',
    download: 'Download pack',
    codes: 'ArmSoft codes',
    codesTitle: 'ArmSoft account codes',
    codesHint:
      'These codes are written on every exported row so the file matches the chart of accounts in ArmSoft. Leave a code blank if you map it there instead.',
    cashDesk: 'Cash desk',
    cardAccount: 'Card / acquiring account',
    vatIncome: 'VAT-taxable income',
    exemptIncome: 'VAT-exempt income',
    turnoverIncome: 'Turnover-tax income',
    purchaseAccount: 'Purchases',
    saved: 'Codes saved.',
    saveFailed: 'Could not save codes.',
    loadFailed: 'Could not load the books.',
    empty: 'Nothing in this period.',
    paid: 'Paid sales',
    cash: 'Cash',
    card: 'Card',
    missingFiscal: 'Missing fiscal receipt',
    fiscalOk: 'Every paid order in this period has a fiscal receipt.',
    fiscalGap:
      '{{count}} paid orders have no fiscal receipt ({{amount}}). Explain that gap before importing.',
    day: 'Date',
    orders: 'Orders',
    gross: 'Gross',
    tips: 'Tips',
    discounts: 'Discounts',
    cogs: 'Food cost',
    fiscalReceipts: 'Fiscal receipts',
    tax: 'Tax',
    returns: 'Returns',
    comps: 'Comps',
    voids: 'Voids',
    purchases: 'Purchases',
    stock: 'Stock differences',
    waste: 'Waste',
    drops: 'Cash drops',
    expenses: 'Drawer expenses',
    b2b: 'Buyer TIN',
    taxType: 'Tax type',
    taxVat: 'VAT taxable',
    taxExempt: 'VAT exempt',
    taxTurnover: 'Turnover tax',
    taxUnknown: 'No department',
    departmentNo: 'Department no.',
    departmentName: 'Department',
    kind: 'Kind',
    kindSale: 'Sale',
    kindReturn: 'Return',
    kindVoid: 'Void',
    kindComp: 'Comp',
    orderCash: 'Order cash',
    orderCard: 'Order card',
    onHand: 'Stock on hand',
    stays: 'Stays',
    checkIn: 'Check-in',
    checkOut: 'Check-out',
    room: 'Room',
    guest: 'Guest',
    nights: 'Nights',
    paymentOption: 'Payment option',
    paymentStatus: 'Payment status',
    tender: 'Tender',
    source: 'Source',
    net: 'Net',
    supplier: 'Supplier',
    tin: 'TIN',
    serial: 'Serial',
    goods: 'Goods',
    qty: 'Quantity',
    unit: 'Unit',
    unitPrice: 'Unit price',
    lineTotal: 'Line total',
    vat: 'VAT',
    invoiceTotal: 'Invoice total',
    ingredient: 'Ingredient',
    systemQty: 'System qty',
    countedQty: 'Counted qty',
    delta: 'Difference',
    value: 'Value at current cost',
    note: 'Note',
    expected: 'Expected',
    counted: 'Counted',
    status: 'Status',
    category: 'Category',
    description: 'Description',
    amount: 'Amount',
    compType: 'Comp type',
    count: 'Count',
    order: 'Order',
    fiscalNo: 'Fiscal number',
    incomeCode: 'Income code',
    accountCode: 'Account code',
    closeReason: 'Close reason',
    sheetSales: 'Sales',
    sheetTax: 'Tax',
    sheetOrders: 'Orders',
    sheetOnHand: 'Stock on hand',
    sheetStays: 'Stays',
    sheetReturns: 'Returns',
    sheetComps: 'Comps',
    sheetVoids: 'Voids',
    sheetPurchases: 'Purchases',
    sheetStock: 'Stock',
    sheetWaste: 'Waste',
    sheetDrops: 'Cash drops',
    sheetExpenses: 'Drawer expenses',
    sheetB2b: 'Buyer TIN',
  },
  staff: {
    title: 'Staff',
    subtitle: 'People and their pay. A new rate starts on the date you choose.',
    add: 'Add staff',
    edit: 'Edit staff',
    empty: 'No staff yet. Add someone to track hours, wages, and comps.',
    noMatch: 'No staff match your search.',
    search: 'Search staff…',
    nameRequired: 'Name is required.',
    colName: 'Name',
    colPay: 'Pay',
    colHours: 'Hours',
    colWage: 'Wages',
    colTip: 'Tip',
    colTotal: 'Total',
    timesheet: 'Timesheet',
    timesheetHint: 'Each row is one shift. End has to be later than start, and shifts can’t overlap.',
    start: 'Start',
    end: 'End',
    prevDay: 'Previous day',
    nextDay: 'Next day',
    prevMonth: 'Previous month',
    nextMonth: 'Next month',
    viewDay: 'Day',
    viewMonth: 'Month',
    monthHint: 'Click a cell to set that person’s hours.',
    openDay: 'Open {{name}} on {{date}}',
    duration: '{{hours}} hrs {{mins}} mins',
    durationHours: '{{hours}} hrs',
    durationMins: '{{mins}} mins',
    fillDrag: 'Drag to copy this cell. An empty cell clears what you cover.',
    off: 'OFF',
    markOff: 'Mark as off',
    offHint: 'This day is a day off. It is not counted as paid hours.',
    overlaps: 'Overlaps',
    showOverlaps: 'Show who is on at the same time',
    overlapEmpty: 'No hours this day.',
    overlapNone: 'No one is on at the same time.',
    copyToChecked: 'Copy times to checked',
    copySlot: 'Copy these times',
    copyTimes: 'Copy times to',
    copyApply: 'Apply',
    copyNoOne: 'No one else on this day.',
    addShift: 'Add another shift',
    sheetSaved: 'Saved',
    sheetIncomplete: 'Each shift needs both a start and an end.',
    sheetOrder: 'End has to be later than start.',
    sheetOverlap: 'Shifts for the same person can’t overlap.',
    payHourly: 'Hourly',
    payShift: 'Per shift',
    payMonthly: 'Monthly',
    perHour: '/ hour',
    perShift: '/ shift',
    perMonth: '/ month',
    rate: 'Rate',
    effectiveFrom: 'Effective from',
    effectiveHint: 'Days before this date keep the previous rate.',
    since: 'Since {{date}}',
    clockIn: 'Clock in',
    clockOut: 'Clock out',
    clockedIn: 'In since {{time}}',
    notIn: 'Not in',
    punches: 'Punches',
    punchesName: '{{name}}',
    newPunch: 'New punch',
    noPunches: 'No punches in this range.',
    clockInAt: 'Clock in',
    clockOutAt: 'Clock out',
    breakMinutes: 'Break (minutes)',
    note: 'Note',
    openPunch: 'Still in',
    punchRequired: 'Clock in is required.',
    punchOrder: 'Clock out must be after clock in.',
    totalPeople: 'People',
    totalHours: 'Hours',
    totalWages: 'Wages',
    wageNote:
      'Tips from paid orders are split by hours. Wages use the rate in effect on each day.',
    deleteTitle: 'Remove {{name}}?',
    deleteBody: 'Their punches are deleted. Past comps stay on the order, without this name.',
    active: 'Active',
    onTimesheet: 'On the timesheet',
    onTimesheetHint: 'Turn this off for someone who only receives comps. They stay on the register and are left out of hours, wages, and tips.',
    compOnly: 'Comp only',
    rateHistory: 'Rate history',
    rateInForce: 'In force',
    rateDeleteTitle: 'Remove the rate from {{date}}?',
    rateDeleteBody: 'Days from that date use the previous rate again.',
    sort: 'Sort order',
  },
} as const;
